import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Future component imports
// import OpportunityTimelineChart from '../../components/dashboard/OpportunityTimelineChart';
// import SavingsForecastChart from '../../components/dashboard/SavingsForecastChart';
// import ResourceAllocationChart from '../../components/dashboard/ResourceAllocationChart';
// import ROIComparisonChart from '../../components/dashboard/ROIComparisonChart';

interface OpportunityInitiative {
  id: string;
  name: string;
  category: string;
  timeframe: 'Quick Win' | 'Mid-term' | 'Long-term';
  implementationTimeMonths: number;
  potentialSavings: number;
  savingsPercentage: number;
  complexity: 'Low' | 'Medium' | 'High';
  resourcesRequired: {
    procurement: number; // FTE required
    technical: number; // FTE required
    businessUnits: number; // FTE required
  };
  implementationStatus: 'Not Started' | 'Planning' | 'In Progress' | 'Completed';
  progress: number; // 0-100%
  roi: number; // Return on investment ratio
}

const OpportunityRoadmapPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [initiatives, setInitiatives] = useState<OpportunityInitiative[]>([
    {
      id: 'OPP001',
      name: 'IT Services Vendor Consolidation',
      category: 'Supplier Consolidation',
      timeframe: 'Mid-term',
      implementationTimeMonths: 6,
      potentialSavings: 1250000,
      savingsPercentage: 10,
      complexity: 'Medium',
      resourcesRequired: {
        procurement: 1.0,
        technical: 0.5,
        businessUnits: 0.25,
      },
      implementationStatus: 'In Progress',
      progress: 65,
      roi: 8.3
    },
    {
      id: 'OPP002',
      name: 'Contract Compliance Improvement',
      category: 'Process Efficiency',
      timeframe: 'Quick Win',
      implementationTimeMonths: 2,
      potentialSavings: 850000,
      savingsPercentage: 15,
      complexity: 'Low',
      resourcesRequired: {
        procurement: 0.5,
        technical: 0.25,
        businessUnits: 0.5,
      },
      implementationStatus: 'Planning',
      progress: 30,
      roi: 12.5
    },
    {
      id: 'OPP003',
      name: 'Logistics Freight Optimization',
      category: 'Price Optimization',
      timeframe: 'Quick Win',
      implementationTimeMonths: 3,
      potentialSavings: 720000,
      savingsPercentage: 14,
      complexity: 'Low',
      resourcesRequired: {
        procurement: 0.75,
        technical: 0.25,
        businessUnits: 0.25,
      },
      implementationStatus: 'Not Started',
      progress: 0,
      roi: 9.8
    },
    {
      id: 'OPP004',
      name: 'Raw Materials Strategic Sourcing',
      category: 'Strategic Sourcing',
      timeframe: 'Long-term',
      implementationTimeMonths: 12,
      potentialSavings: 1850000,
      savingsPercentage: 22,
      complexity: 'High',
      resourcesRequired: {
        procurement: 1.5,
        technical: 1.0,
        businessUnits: 1.0,
      },
      implementationStatus: 'Not Started',
      progress: 0,
      roi: 7.2
    },
    {
      id: 'OPP005',
      name: 'Tail Spend Management Program',
      category: 'Tail Spend',
      timeframe: 'Mid-term',
      implementationTimeMonths: 8,
      potentialSavings: 950000,
      savingsPercentage: 19,
      complexity: 'Medium',
      resourcesRequired: {
        procurement: 1.0,
        technical: 0.5,
        businessUnits: 0.5,
      },
      implementationStatus: 'Planning',
      progress: 15,
      roi: 6.8
    }
  ]);

  // Business Rules Implementation
  const prioritizationScores = initiatives.map(init => {
    const complexityScore = init.complexity === 'Low' ? 3 : init.complexity === 'Medium' ? 2 : 1;
    const timeframeScore = init.timeframe === 'Quick Win' ? 3 : init.timeframe === 'Mid-term' ? 2 : 1;
    const savingsScore = init.potentialSavings > 1000000 ? 3 : init.potentialSavings > 500000 ? 2 : 1;
    const roiScore = init.roi > 10 ? 3 : init.roi > 8 ? 2 : 1;
    
    const totalScore = complexityScore + timeframeScore + savingsScore + roiScore;
    
    return {
      id: init.id,
      name: init.name,
      totalScore,
      ranking: 0 // Will be set after sorting
    };
  }).sort((a, b) => b.totalScore - a.totalScore)
    .map((item, index) => ({ ...item, ranking: index + 1 }));

  // Calculate total potential savings
  const totalPotentialSavings = initiatives.reduce((sum, init) => sum + init.potentialSavings, 0);
  
  // Calculate resource requirements
  const totalResourceRequirements = {
    procurement: initiatives.reduce((sum, init) => sum + init.resourcesRequired.procurement, 0),
    technical: initiatives.reduce((sum, init) => sum + init.resourcesRequired.technical, 0),
    businessUnits: initiatives.reduce((sum, init) => sum + init.resourcesRequired.businessUnits, 0)
  };

  // Available resources (example)
  const availableResources = {
    procurement: 2.5,
    technical: 1.5,
    businessUnits: 1.5
  };

  // Check for resource constraints
  const resourceConstraints = {
    procurement: totalResourceRequirements.procurement > availableResources.procurement,
    technical: totalResourceRequirements.technical > availableResources.technical,
    businessUnits: totalResourceRequirements.businessUnits > availableResources.businessUnits
  };

  // Group initiatives by timeframe for timeline view
  const quickWins = initiatives.filter(init => init.timeframe === 'Quick Win');
  const midTerm = initiatives.filter(init => init.timeframe === 'Mid-term');
  const longTerm = initiatives.filter(init => init.timeframe === 'Long-term');

  // Helper function to render progress with color coding
  const renderProgress = (progress: number) => {
    let color = 'primary';
    if (progress < 25) {
      color = 'error';
    } else if (progress < 75) {
      color = 'warning';
    } else {
      color = 'success';
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color={color as any}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{progress}%</Typography>
        </Box>
      </Box>
    );
  };

  // Helper function to get background color based on implementation status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'rgba(76, 175, 80, 0.1)';
      case 'In Progress':
        return 'rgba(33, 150, 243, 0.1)';
      case 'Planning':
        return 'rgba(255, 152, 0, 0.1)';
      default:
        return 'rgba(158, 158, 158, 0.1)';
    }
  };

  // Helper function to get status chip color
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'primary';
      case 'Planning':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Opportunity Roadmap
      </Typography>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Total Savings Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="success" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">Total Potential Savings</Typography>
                  </Box>
                  <Typography variant="h4" color="success.main" sx={{ mb: 1 }}>
                    ${(totalPotentialSavings/1000000).toFixed(1)}M
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Across {initiatives.length} identified initiatives
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Implementation Timeline Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">Implementation Timeline</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">
                      <Typography component="span" fontWeight="bold" color="success.main">
                        {quickWins.length}
                      </Typography> Quick Wins ({quickWins.reduce((sum, i) => sum + i.potentialSavings, 0)/1000000}M)
                    </Typography>
                    <Typography variant="subtitle1">
                      <Typography component="span" fontWeight="bold" color="primary.main">
                        {midTerm.length}
                      </Typography> Mid-term ({midTerm.reduce((sum, i) => sum + i.potentialSavings, 0)/1000000}M)
                    </Typography>
                    <Typography variant="subtitle1">
                      <Typography component="span" fontWeight="bold" color="secondary.main">
                        {longTerm.length}
                      </Typography> Long-term ({longTerm.reduce((sum, i) => sum + i.potentialSavings, 0)/1000000}M)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Resource Allocation Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PlayArrowIcon color="warning" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">Resource Requirements</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Procurement:</span> 
                      <span style={{ color: resourceConstraints.procurement ? theme.palette.error.main : 'inherit' }}>
                        {totalResourceRequirements.procurement.toFixed(1)} FTE / {availableResources.procurement} available
                      </span>
                    </Typography>
                    <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Technical:</span> 
                      <span style={{ color: resourceConstraints.technical ? theme.palette.error.main : 'inherit' }}>
                        {totalResourceRequirements.technical.toFixed(1)} FTE / {availableResources.technical} available
                      </span>
                    </Typography>
                    <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Business Units:</span> 
                      <span style={{ color: resourceConstraints.businessUnits ? theme.palette.error.main : 'inherit' }}>
                        {totalResourceRequirements.businessUnits.toFixed(1)} FTE / {availableResources.businessUnits} available
                      </span>
                    </Typography>
                  </Box>
                  {(resourceConstraints.procurement || resourceConstraints.technical || resourceConstraints.businessUnits) && (
                    <Typography variant="caption" color="error">
                      Resource constraints detected. Prioritization required.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Timeline Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Opportunity Timeline
              {/* <OpportunityTimelineChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Savings Forecast Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Savings Forecast (Next 24 Months)
              {/* <SavingsForecastChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Resource Allocation Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Resource Allocation by Initiative
              {/* <ResourceAllocationChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Initiatives Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Savings Initiatives
                </Typography>
                <Button variant="contained" color="primary">
                  Export Roadmap
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Initiative</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Timeframe</TableCell>
                      <TableCell align="right">Potential Savings</TableCell>
                      <TableCell align="right">ROI</TableCell>
                      <TableCell>Complexity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {initiatives.map((initiative, index) => {
                      const priority = prioritizationScores.find(p => p.id === initiative.id)?.ranking || 0;
                      return (
                        <TableRow 
                          key={initiative.id}
                          sx={{ 
                            backgroundColor: getStatusColor(initiative.implementationStatus)
                          }}
                        >
                          <TableCell>{priority}</TableCell>
                          <TableCell>{initiative.name}</TableCell>
                          <TableCell>{initiative.category}</TableCell>
                          <TableCell>
                            <Chip 
                              label={initiative.timeframe} 
                              size="small"
                              color={
                                initiative.timeframe === 'Quick Win' ? 'success' : 
                                initiative.timeframe === 'Mid-term' ? 'primary' : 'secondary'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            ${(initiative.potentialSavings/1000).toFixed(0)}K ({initiative.savingsPercentage}%)
                          </TableCell>
                          <TableCell align="right">{initiative.roi}x</TableCell>
                          <TableCell>
                            <Chip 
                              label={initiative.complexity} 
                              size="small"
                              color={
                                initiative.complexity === 'Low' ? 'success' : 
                                initiative.complexity === 'Medium' ? 'warning' : 'error'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={initiative.implementationStatus} 
                              size="small"
                              color={getStatusChipColor(initiative.implementationStatus) as any}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            {renderProgress(initiative.progress)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OpportunityRoadmapPage; 